/**
 * 数字金额 → 人民币中文大写（财务单据常用）。
 * 支持到「分」，最大到万亿级；负数加「负」，零金额「零元整」。
 */
const DIGITS = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
const UNITS = ['', '拾', '佰', '仟'];
const BIG_UNITS = ['', '万', '亿', '兆'];

/**
 * 整数部分 → 中文大写。返回 null 表示超出可靠范围（位数超过「兆」节，
 * 或已经越过 JS 整数精度），由调用方原样输出，绝不能拼出 undefined。
 *
 * 「零」有两种来源，缺一不可（少了任何一种，10005 会印成「壹万伍元」，
 * 银行和审计都会退单）：
 *   1. 节内断层：壹佰「零」伍
 *   2. 跨节：本节以 0 开头而更高节已有内容 —— 壹万「零」伍
 */
function intToRmb(intPart: string): string | null {
  if (/^0*$/.test(intPart)) return '';
  // 按 4 位分节
  const groups: string[] = [];
  let s = intPart;
  while (s.length > 0) {
    groups.unshift(s.slice(-4));
    s = s.slice(0, -4);
  }
  if (groups.length > BIG_UNITS.length) return null;

  let out = '';
  for (let gi = 0; gi < groups.length; gi++) {
    const bigUnit = BIG_UNITS[groups.length - 1 - gi];
    const digits = groups[gi].padStart(4, '0').split('').map(Number);
    let seg = '';
    let zeroPending = false;
    digits.forEach((d, di) => {
      if (d === 0) {
        zeroPending = true;
        return;
      }
      // 节内断层补零；本节开头的零留给下面的跨节逻辑，免得重复
      if (zeroPending && seg !== '') seg += '零';
      zeroPending = false;
      seg += DIGITS[d] + UNITS[3 - di];
    });

    if (seg === '') {
      // 整节全 0（如 1_0000_1234 的中间节）：占一个零，等后面的节接上
      if (out !== '' && !out.endsWith('零')) out += '零';
      continue;
    }
    // 跨节的零：壹万「零」伍
    if (digits[0] === 0 && out !== '' && !out.endsWith('零')) out += '零';
    out += seg + bigUnit;
  }
  return out.replace(/零+$/, '');
}

export function toRmbUpper(value: unknown): string {
  // 空值必须原样返回空 —— 以前空字符串会被 Number('') 变成 0，在正式单据上
  // 印出「零元整」，比留空危险得多。非数字文本（「待定」）同样原样保留。
  const raw = String(value ?? '').trim();
  const cleaned = raw.replace(/[^0-9.-]/g, '');
  if (!/[0-9]/.test(cleaned)) return raw;
  const num = Number(cleaned);
  if (!Number.isFinite(num)) return raw;
  const neg = num < 0;
  const fixed = Math.abs(num).toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const jiao = Number(decPart[0]);
  const fen = Number(decPart[1]);

  const intText = intToRmb(intPart);
  if (intText === null) return raw; // 超出「兆」节或精度范围，原样输出
  let result = intText;
  if (result) result += '元';

  if (jiao === 0 && fen === 0) {
    result = result ? result + '整' : '零元整';
  } else {
    if (jiao > 0) result += DIGITS[jiao] + '角';
    else if (result) result += '零'; // 元与分之间的零
    if (fen > 0) result += DIGITS[fen] + '分';
  }
  if (!result) result = '零元整';
  return (neg ? '负' : '') + result;
}
